import { forwardRef, useEffect, useRef, type InputHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

// Native-checkbox wrapper. Stays a real <input type="checkbox"> for forms +
// a11y semantics, but `appearance: none` strips the OS chrome so we render
// a consistent design across light/dark mode and Windows/macOS — the native
// unchecked square renders as a filled black box on some Windows themes,
// which is what prompted the rewrite.
//
// Visual design:
//   - Unchecked: white surface, 1.5px border in the design-system border
//     color. Matches input-field borders elsewhere.
//   - Checked: lime fill, ink check mark via SVG mask.
//   - Indeterminate: lime fill, ink horizontal bar.
//   - Focus: 2px lime ring drawn via box-shadow so it follows the rounded
//     corners cleanly (same trick as .modal__close).
//
// The `peer` / sibling pattern isn't used — we use ::after on the input
// itself with a background-image SVG so the markup stays one element.

interface Props
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  indeterminate?: boolean;
}

export const Checkbox = forwardRef<HTMLInputElement, Props>(
  ({ indeterminate, className, ...props }, forwardedRef) => {
    const innerRef = useRef<HTMLInputElement>(null);

    // Sync the indeterminate property — React doesn't surface it as a prop
    // because it isn't an HTML attribute. Apply it after every render so a
    // checked-state flip doesn't strand a stale indeterminate flag.
    useEffect(() => {
      if (innerRef.current) {
        innerRef.current.indeterminate = !!indeterminate;
      }
    }, [indeterminate]);

    return (
      <input
        type="checkbox"
        ref={(node) => {
          (innerRef as { current: HTMLInputElement | null }).current = node;
          if (typeof forwardedRef === 'function') forwardedRef(node);
          else if (forwardedRef)
            (forwardedRef as { current: HTMLInputElement | null }).current = node;
        }}
        className={cn('sa-checkbox', className)}
        {...props}
      />
    );
  },
);
Checkbox.displayName = 'Checkbox';
